import { BellOff, type LucideIcon, Volume2, VolumeX } from "lucide-react";
import { useId } from "react";
import { Link } from "react-router-dom";

import { useNavContext } from "@/app/auth/useNavContext";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaButtonGroup } from "@/shared/components/buttons/ColocaButtonGroup";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { availableDashboardAlertTypes } from "@/shared/dashboard-alerts/dashboardAlertAvailability";
import {
  DASHBOARD_ALERT_CATALOG,
  DASHBOARD_ALERT_GROUPS,
  type DashboardAlertType,
} from "@/shared/dashboard-alerts/dashboardAlertCatalog";
import {
  DASHBOARD_ALERT_GROUP_SOUND_DESCRIPTION,
  DASHBOARD_ALERT_GROUP_TITLE,
  DASHBOARD_ALERT_TYPE_ICON,
} from "@/shared/dashboard-alerts/dashboardAlertDisplay";
import { DashboardAlertGroupSoundButtons } from "@/shared/dashboard-alerts/DashboardAlertGroupSound";
import {
  DASHBOARD_ALERT_MODES,
  type DashboardAlertMode,
  useDashboardAlertPreferences,
} from "@/shared/dashboard-alerts/dashboardAlertPreferences";
import { useUnlockDashboardAlertSound } from "@/shared/dashboard-alerts/dashboardAlertPresenter";
import { playDashboardAlertSound } from "@/shared/dashboard-alerts/dashboardAlertSounds";
import { useBrebReceivingKey } from "@/shared/hooks/useBrebReceivingKey";
import type { DashboardAlertsTranslations } from "@/shared/lang/interfaces/dashboardAlerts";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { SettingsSection } from "./SettingsSection";

export const DASHBOARD_ALERTS_SECTION_ID = "avisos";

type AlertsKey = keyof DashboardAlertsTranslations;

const MODE_LABEL: Record<DashboardAlertMode, AlertsKey> = {
  sound: "mode_sound",
  silent: "mode_silent",
  off: "mode_off",
};

const MODE_ICON: Record<DashboardAlertMode, LucideIcon> = { sound: Volume2, silent: VolumeX, off: BellOff };

// The icon's chip says whether the alert is good news or a problem; grey when it is turned off.
const OUTCOME_CHIP = {
  good: "bg-success/10 text-success",
  problem: "bg-destructive/10 text-destructive",
} as const;
const OFF_CHIP = "bg-muted text-muted-foreground";

/**
 * Settings → Avisos en el dashboard: per alert, with its sound, silent or off (with sound by default),
 * grouped by module, each module with its own sound to listen to; and one master switch that silences them
 * all without touching those choices. Only the modules the person can read are listed, since an alert for the
 * others would never arrive. Remembered per browser (`dashboardAlertPreferences`).
 */
export function DashboardAlertsSection() {
  const navContext = useNavContext();
  // The organization's Bre-B keys are read only where their alert could be offered at all: a member who cannot
  // read payments would get a 403 for nothing.
  const keysMatter = availableDashboardAlertTypes(navContext, { brebKeys: true }).includes("payins_breb_key");
  return keysMatter ? (
    <DashboardAlertsSectionWithBrebKeys />
  ) : (
    <DashboardAlertsSectionBody brebKeys={false} />
  );
}

function DashboardAlertsSectionWithBrebKeys() {
  const { state } = useBrebReceivingKey();
  // Known once the organization is seen to have a key or be allowed one; until then the row stays out, so it
  // never appears and vanishes.
  const brebKeys = state ? state.keys.length > 0 || state.eligibility.enabled : null;
  return <DashboardAlertsSectionBody brebKeys={brebKeys} />;
}

function DashboardAlertsSectionBody({ brebKeys }: { brebKeys: boolean | null }) {
  const { t } = useTranslations("dashboardAlerts");
  const navContext = useNavContext();
  const { sound, modeOf, setSound, setMode } = useDashboardAlertPreferences();
  const soundSwitchLabel = useId();
  const soundSwitchHint = useId();
  useUnlockDashboardAlertSound();

  // A row for every alert this person can get: the pages the sidebar shows them.
  const available = availableDashboardAlertTypes(navContext, { brebKeys }).filter(
    (type) => type !== "payins_breb_key" || brebKeys === true,
  );
  const groups = DASHBOARD_ALERT_GROUPS.map((group) => ({
    ...group,
    types: group.types.filter((type) => available.includes(type)),
  })).filter((group) => group.types.length > 0);
  const types = groups.flatMap((group) => group.types);
  const payments = groups.some((group) => group.id === "payins" || group.id === "payouts");
  const cards = groups.some((group) => group.id === "cards");
  const on = types.filter((type) => modeOf(type) !== "off").length;

  return (
    <SettingsSection
      id={DASHBOARD_ALERTS_SECTION_ID}
      eyebrow={t("section_eyebrow")}
      title={t("section_title")}
      sub={t("section_sub")}
      headerRight={
        <ColocaBadge variant={on > 0 ? "success" : "secondary"}>
          {t("section_count", { on, total: types.length })}
        </ColocaBadge>
      }
    >
      <div className="flex items-start gap-3 rounded-xl border border-border px-3.5 py-3">
        <span
          aria-hidden="true"
          className={cn(
            "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors",
            sound ? OUTCOME_CHIP.good : OFF_CHIP,
          )}
        >
          {sound ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <p id={soundSwitchLabel} className="text-[13px] font-medium text-foreground">
            {t("sound_label")}
          </p>
          <p id={soundSwitchHint} className="mt-0.5 text-[11.5px] text-muted-foreground">
            {t("sound_hint")}
          </p>
        </div>
        <span className="mt-1 shrink-0">
          <ColocaSwitch
            checked={sound}
            onCheckedChange={(next) => {
              // Turning it on plays the coin, so the person knows the sound works.
              if (next) playDashboardAlertSound("payin", { force: true });
              setSound(next);
            }}
            aria-labelledby={soundSwitchLabel}
            aria-describedby={soundSwitchHint}
          />
        </span>
      </div>

      {groups.map((group) => (
        <section key={group.id} aria-labelledby={`alerts-group-${group.id}`} className="mt-5">
          <div className="mb-2 flex flex-wrap items-end justify-between gap-x-3 gap-y-1.5">
            <div className="min-w-0">
              <h3
                id={`alerts-group-${group.id}`}
                className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground"
              >
                {t(DASHBOARD_ALERT_GROUP_TITLE[group.id])}
              </h3>
              <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                {t(DASHBOARD_ALERT_GROUP_SOUND_DESCRIPTION[group.id])}
              </p>
            </div>
            <DashboardAlertGroupSoundButtons group={group.id} />
          </div>
          <div className="divide-y divide-border rounded-xl border border-border">
            {group.types.map((type) => (
              <AlertModeRow
                key={type}
                type={type}
                mode={modeOf(type)}
                soundMuted={!sound}
                onChange={(mode) => {
                  if (mode === "sound" && sound) {
                    playDashboardAlertSound(DASHBOARD_ALERT_CATALOG[type].sound, { force: true });
                  }
                  setMode(type, mode);
                }}
              />
            ))}
          </div>
        </section>
      ))}

      <div className="mt-5 space-y-1.5 text-xs text-muted-foreground">
        <p>{t("browser_note")}</p>
        {(payments || cards) && (
          <p>
            {t("whatsapp_note")}{" "}
            {payments && (
              <Link
                to="/app/wallets/notifications"
                className="font-medium text-primary underline-offset-4 hover:underline dark:text-success"
              >
                {t("whatsapp_payments_link")}
              </Link>
            )}
            {payments && cards && " · "}
            {cards && (
              <Link
                to="/app/cards/notifications"
                className="font-medium text-primary underline-offset-4 hover:underline dark:text-success"
              >
                {t("whatsapp_cards_link")}
              </Link>
            )}
          </p>
        )}
      </div>
    </SettingsSection>
  );
}

interface AlertModeRowProps {
  type: DashboardAlertType;
  mode: DashboardAlertMode;
  /** The master switch is off: "with sound" is still the choice, it just does not play right now. */
  soundMuted: boolean;
  onChange: (mode: DashboardAlertMode) => void;
}

function AlertModeRow({ type, mode, soundMuted, onChange }: AlertModeRowProps) {
  const { t } = useTranslations("dashboardAlerts");
  const Icon = DASHBOARD_ALERT_TYPE_ICON[type];
  const label = t(`type_${type}` as AlertsKey);
  const hintId = useId();
  return (
    <div className="flex flex-col gap-2.5 px-3.5 py-3 sm:flex-row sm:items-start sm:gap-3">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span
          aria-hidden="true"
          className={cn(
            "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors",
            mode === "off" ? OFF_CHIP : OUTCOME_CHIP[DASHBOARD_ALERT_CATALOG[type].outcome],
          )}
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-foreground">{label}</p>
          <p id={hintId} className="mt-0.5 text-[11.5px] text-muted-foreground">
            {t(`type_${type}_hint` as AlertsKey)}
          </p>
        </div>
      </div>
      {/* Under the text on a phone (lined up with it), beside it from `sm` up. */}
      <ColocaButtonGroup
        aria-label={t("mode_group_aria", { label })}
        aria-describedby={hintId}
        className="ml-11 shrink-0 flex-wrap self-start sm:ml-0"
      >
        {DASHBOARD_ALERT_MODES.map((option) => {
          const selected = option === mode;
          return (
            <ColocaButton
              key={option}
              size="xs"
              variant={selected ? "secondary" : "default"}
              aria-pressed={selected}
              icon={MODE_ICON[option]}
              className={cn(
                selected ? "font-semibold" : "text-muted-foreground",
                // Chosen, but the master switch keeps it quiet for now.
                selected && option === "sound" && soundMuted && "line-through decoration-muted-foreground/60",
              )}
              onClick={() => {
                if (!selected) onChange(option);
              }}
            >
              {t(MODE_LABEL[option])}
            </ColocaButton>
          );
        })}
      </ColocaButtonGroup>
    </div>
  );
}
